import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const ORG_TYPES = [
  { value: 'all', label: 'All causes' },
  { value: 'homeless_shelter', label: 'Homeless shelter' },
  { value: 'youth_center', label: 'Youth center' },
  { value: 'diaper_bank', label: 'Diaper bank' },
  { value: 'domestic_violence', label: 'Domestic violence' },
  { value: 'transitional_housing', label: 'Transitional housing' },
  { value: 'food_pantry', label: 'Food pantry' },
  { value: 'family_services', label: 'Family services' },
  { value: 'other', label: 'Other nonprofit' }
];


const ORG_TYPE_LABELS = ORG_TYPES.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

function BrowseOrgs() {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgType, setOrgType] = useState('all');
  const [search, setSearch] = useState('');
  const [hasOpenNeedsOnly, setHasOpenNeedsOnly] = useState(false);
  const [sortBy, setSortBy] = useState('open_needs');

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [orgsSnap, needsSnap] = await Promise.all([
        getDocs(query(collection(db, 'organizations'), where('status', '==', 'active'))),
        getDocs(query(collection(db, 'needs'), where('status', '==', 'open')))
      ]);

      setOrgs(orgsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setNeeds(needsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading organizations:', err);
    } finally {
      setLoading(false);
    }
  }

  const openNeedCounts = useMemo(() => {
    const counts = {};
    needs.forEach(need => {
      if (!need.orgId) return;
      counts[need.orgId] = (counts[need.orgId] || 0) + 1;
    });
    return counts;
  }, [needs]);

  const filtered = useMemo(() => {
    let result = [...orgs];

    if (orgType !== 'all') {
      result = result.filter(org => org.orgType === orgType);
    }

    if (hasOpenNeedsOnly) {
      result = result.filter(org => (openNeedCounts[org.id] || 0) > 0);
    }

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      result = result.filter(org => {
        return [
          org.orgName,
          org.missionStatement,
          org.populationsServed,
          org.city,
          ORG_TYPE_LABELS[org.orgType]
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
    }

    result.sort((a, b) => {
      if (sortBy === 'open_needs') {
        return (openNeedCounts[b.id] || 0) - (openNeedCounts[a.id] || 0);
      }

      if (sortBy === 'name') {
        return (a.orgName || '').localeCompare(b.orgName || '');
      }

      if (sortBy === 'newest') {
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }

      return 0;
    });

    return result;
  }, [orgs, orgType, hasOpenNeedsOnly, search, sortBy, openNeedCounts]);

  function resetFilters() {
    setOrgType('all');
    setSearch('');
    setHasOpenNeedsOnly(false);
    setSortBy('open_needs');
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f9fafb',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
    }}>
      <header style={{
        background: 'white',
        borderBottom: '1px solid #eee',
        padding: '0 2rem',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div
          style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#2d6a4f',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        >
          somebody.gives
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>

  {currentUser && userRole === 'donor' ? (
    <>
      <button
        onClick={() => navigate('/give/dashboard')}
        style={{
          background: 'transparent',
          color: '#2d6a4f',
          border: '2px solid #2d6a4f',
          padding: '0.5rem 1rem',
          borderRadius: '8px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          fontWeight: 600
        }}
      >
        My dashboard
      </button>

      <button
        onClick={() => navigate('/give/browse')}
        style={{
          background: 'transparent',
          color: '#2d6a4f',
          border: '2px solid #2d6a4f',
          padding: '0.5rem 1rem',
          borderRadius: '8px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          fontWeight: 600
        }}
      >
        Browse needs
      </button>

      <button
        onClick={() => navigate('/give/sponsorships')}
        style={{
          background: '#2d6a4f',
          color: 'white',
          border: 'none',
          padding: '0.5rem 1rem',
          borderRadius: '8px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          fontWeight: 600
        }}
      >
        Browse sponsorships
      </button>
    </>
  ) : (
    <>
      <button
        onClick={() => navigate('/give/browse-public')}
        style={{
          background: 'transparent',
          color: '#2d6a4f',
          border: '2px solid #2d6a4f',
          padding: '0.5rem 1rem',
          borderRadius: '8px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          fontWeight: 600
        }}
      >
        Browse needs
      </button>

      <button
        onClick={() => navigate('/give/browse-sponsorships')}
        style={{
          background: 'transparent',
          color: '#2d6a4f',
          border: '2px solid #2d6a4f',
          padding: '0.5rem 1rem',
          borderRadius: '8px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          fontWeight: 600
        }}
      >
        Browse sponsorships
      </button>

      <button
        onClick={() => navigate('/give/signup')}
        style={{
          background: '#2d6a4f',
          color: 'white',
          border: 'none',
          padding: '0.5rem 1rem',
          borderRadius: '8px',
          fontSize: '0.9rem',
          cursor: 'pointer',
          fontWeight: 600
        }}
      >
        Get started
      </button>
    </>
  )}

</div>
      </header>

      <main style={{ maxWidth: '1000px', margin: '0 auto', padding: '2.5rem 2rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.9rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            Browse nonprofit partners
          </h1>
          <p style={{ color: '#555', fontSize: '0.95rem', maxWidth: '680px' }}>
            Find vetted Springfield nonprofits, see what they need, and take responsibility for recurring supplies.
          </p>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          border: '1px solid #eee',
          marginBottom: '1.25rem',
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search organizations..."
            style={{
              padding: '0.55rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontSize: '0.9rem',
              minWidth: '220px',
              flex: '1 1 240px'
            }}
          />

          <select
            value={orgType}
            onChange={e => setOrgType(e.target.value)}
            style={{
              padding: '0.55rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontSize: '0.9rem',
              background: 'white'
            }}
          >
            {ORG_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{
              padding: '0.55rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontSize: '0.9rem',
              background: 'white'
            }}
          >
            <option value="open_needs">Open needs first</option>
            <option value="name">A-Z</option>
            <option value="newest">Newest partners</option>
          </select>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.85rem',
            color: '#555',
            fontWeight: 600
          }}>
            <input
              type="checkbox"
              checked={hasOpenNeedsOnly}
              onChange={e => setHasOpenNeedsOnly(e.target.checked)}
            />
            Has open needs
          </label>

          <button
            onClick={resetFilters}
            style={{
              background: 'white',
              border: '1px solid #ddd',
              color: '#555',
              padding: '0.5rem 0.85rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Reset
          </button>
        </div>

        {!loading && (
          <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {filtered.length} organization{filtered.length !== 1 ? 's' : ''} found
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
            Loading organizations...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{
            background: 'white',
            border: '1px solid #eee',
            borderRadius: '12px',
            padding: '3rem',
            textAlign: 'center',
            color: '#888'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔍</div>
            <div style={{ fontWeight: 700, color: '#333', marginBottom: '0.5rem' }}>
              No organizations match your filters
            </div>
            <button
              onClick={resetFilters}
              style={{
                marginTop: '1rem',
                background: '#2d6a4f',
                color: 'white',
                border: 'none',
                padding: '0.65rem 1.2rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Clear filters
            </button>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
          gap: '1rem'
        }}>
          {!loading && filtered.map(org => {
            const openCount = openNeedCounts[org.id] || 0;

            return (
              <div
                key={org.id}
                style={{
                  background: 'white',
                  border: '1px solid #eee',
                  borderRadius: '14px',
                  padding: '1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '260px'
                }}
              >
                <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {org.logoUrl ? (
                      <img
                        src={org.logoUrl}
                        alt={`${org.orgName} logo`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <span style={{
                        color: '#2d6a4f',
                        fontWeight: 800,
                        fontSize: '1.2rem'
                      }}>
                        {(org.orgName || '?').charAt(0)}
                      </span>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{
                      color: '#2d6a4f',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      marginBottom: '0.25rem'
                    }}>
                      {ORG_TYPE_LABELS[org.orgType] || 'Nonprofit'}
                    </div>

                    <h2 style={{
                      fontSize: '1.1rem',
                      lineHeight: 1.25,
                      margin: 0,
                      color: '#222'
                    }}>
                      {org.orgName}
                    </h2>
                  </div>
                </div>

                {org.missionStatement && (
                  <p style={{
                    color: '#555',
                    fontSize: '0.9rem',
                    lineHeight: 1.45,
                    marginTop: '1rem',
                    marginBottom: '0.75rem'
                  }}>
                    {org.missionStatement.length > 150
                      ? `${org.missionStatement.slice(0, 150)}...`
                      : org.missionStatement}
                  </p>
                )}

                {org.populationsServed && (
                  <div style={{
                    color: '#666',
                    fontSize: '0.82rem',
                    marginBottom: '1rem'
                  }}>
                    <strong>Serves:</strong> {org.populationsServed}
                  </div>
                )}

                <div style={{ marginTop: 'auto' }}>
                  <div style={{
                    fontSize: '0.85rem',
                    color: openCount > 0 ? '#2d6a4f' : '#888',
                    fontWeight: 700,
                    marginBottom: '0.75rem'
                  }}>
                    {openCount} open need{openCount !== 1 ? 's' : ''}
                  </div>

                  <button
                    onClick={() => navigate(`/o/${org.id}`)}
                    style={{
                      width: '100%',
                      background: '#1e3a5f',
                      color: 'white',
                      border: 'none',
                      padding: '0.7rem 1rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '0.9rem'
                    }}
                  >
                    View needs
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <footer style={{
        textAlign: 'center',
        padding: '2rem',
        color: '#888',
        fontSize: '0.85rem',
        borderTop: '1px solid #eee',
        background: 'white',
        marginTop: '2rem'
      }}>
        <span style={{ fontWeight: 700, color: '#555' }}>
          somebody.gives
        </span>
        {' '}&bull; Currently serving Springfield, MO
      </footer>
    </div>
  );
}

export default BrowseOrgs;